import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { AmTeamSettings, AmThresholds } from "../../lib/crm-types";
import { Panel } from "../../components/ui/Panel";
import { cn } from "../../components/ui/cn";
import { SavedFlash, inputClass } from "./shared";

/**
 * When an account counts as at risk.
 *
 * ## One set of rules per sales desk, not one for the company
 *
 * The brief asks for it and the reason holds. Internal sales work a high volume
 * of accounts that order weekly, so thirty days of silence is a problem;
 * external sales work fewer, larger accounts on longer cycles, where the same
 * rule would flag the whole book every month and teach everybody to ignore the
 * flag. A single threshold across both desks has to be wrong for one of them.
 *
 * An individual account can still override the two day thresholds on its own
 * page -- a builders' merchant ordering weekly and a specialist ordering twice
 * a year are not both lapsed at thirty days. The decline percentage cannot be
 * overridden per account on purpose: what counts as a worrying drop is a
 * desk-wide judgement, and a per-account dial would be a way to quietly silence
 * awkward accounts.
 *
 * Saves on blur with no Save button, matching every other settings panel here.
 */
export function AtRiskSettings({ isAdmin }: { isAdmin: boolean }) {
  const client = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const settings = useQuery({
    queryKey: queryKeys.crmAmSettings,
    queryFn: crmApi.amSettings,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: ({
      teamSlug,
      patch,
    }: {
      teamSlug: string;
      patch: Partial<AmThresholds>;
    }) => crmApi.saveAmSettings(teamSlug, patch),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.crmAmSettings });
      /* The whole CRM root, not just this key. Changing a threshold re-decides
         which accounts are at risk, so every figure on the dashboard -- the
         quiet count, the contact-next order, every flag -- is now wrong until
         it refetches. */
      void client.invalidateQueries({ queryKey: ["crm"] });
      setSavedAt(Date.now());
    },
  });

  const rows = settings.data?.settings ?? [];

  return (
    <Panel collapsible title="When an account needs attention">
      {/*
        Said once, above the grid. Three sentences of preamble per team was
        what made the stacked version hard to read, and the one thing a reader
        genuinely needs to know -- that this is a blanket default rather than
        the last word -- belongs at the top rather than repeated in every row.
      */}
      <p className="mb-3 text-xs text-muted">
        The default for every account on each desk. An account manager can
        override the two day thresholds on any individual company, from its own
        page.
      </p>

      {settings.isLoading ? (
        <p className="text-xs text-faint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-faint">No sales teams are configured.</p>
      ) : (
        /* Scrolls inside itself rather than widening the settings page, which
           is a fixed column shared with four other panels.

           On a phone the table is not a table: each team is a name over its
           three boxes, each box labelled. Four columns needed some 460px, so
           at 390 "Spend down" sat off the side of a box that scrolled with no
           sign that it did -- a threshold nobody would know was there. */
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm max-md:block">
            <thead className="text-left text-[11px] uppercase tracking-wide text-faint max-md:hidden">
              <tr>
                <th className="px-2 py-1.5 font-medium">Sales team</th>
                <th className="px-2 py-1.5 text-right font-medium">
                  No order for
                </th>
                <th
                  className="px-2 py-1.5 text-right font-medium"
                  title="Counts real contact only -- a note, a call, an email, a meeting, a quote or an order. Changing the account manager or a field on the record does not reset it."
                >
                  No contact for
                </th>
                <th
                  className="px-2 py-1.5 text-right font-medium"
                  title="Needs Team connected. Until then this rule never fires, and no account is flagged for a falling spend nobody can measure."
                >
                  Spend down
                </th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {rows.map((row) => (
                <TeamRules
                  key={row.teamSlug}
                  row={row}
                  disabled={!isAdmin}
                  onSave={(patch) =>
                    save.mutate({ teamSlug: row.teamSlug, patch })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <SavedFlash pending={save.isPending} at={savedAt} />
      </div>

      {save.isError ? (
        <p className="mt-2 text-xs text-sla-breached">
          {save.error instanceof ApiError
            ? save.error.message
            : "Could not save those rules."}
        </p>
      ) : null}
    </Panel>
  );
}

/** One desk's row: the name, then a box per rule under its own heading. */
function TeamRules({
  row,
  disabled,
  onSave,
}: {
  row: AmTeamSettings;
  disabled: boolean;
  onSave: (patch: Partial<AmThresholds>) => void;
}) {
  return (
    <tr className="border-t border-line max-md:grid max-md:grid-cols-3 max-md:gap-x-2 max-md:py-2">
      <td className="px-2 py-1.5 max-md:col-span-3 max-md:px-0 max-md:pb-1 max-md:pt-0">
        <span className="text-sm text-content">{teamName(row.teamSlug)}</span>
        {/* Says out loud that nothing has been chosen yet, so a manager can
            tell "we decided on 30" from "nobody has ever looked at this". */}
        {!row.configured ? (
          <span className="ml-1.5 text-[10px] text-faint">Using defaults</span>
        ) : null}
      </td>

      <Cell
        label="No order"
        value={row.noOrderDays}
        unit="days"
        disabled={disabled}
        onSave={(value) => onSave({ noOrderDays: value })}
      />
      <Cell
        label="No contact"
        value={row.noContactDays}
        unit="days"
        disabled={disabled}
        onSave={(value) => onSave({ noContactDays: value })}
      />
      <Cell
        label="Spend down"
        value={row.spendDeclinePercent}
        unit="%"
        max={100}
        disabled={disabled}
        onSave={(value) => onSave({ spendDeclinePercent: value })}
      />
    </tr>
  );
}

/**
 * One threshold.
 *
 * Uncontrolled and keyed on the stored value, exactly as `SalesSettings.Rule`
 * is: the box re-seeds when the query lands, and typing is never fought by a
 * background refetch. The unit sits beside the box rather than in the header
 * alone, because a column of bare numbers is ambiguous the moment somebody
 * scrolls the heading out of view.
 */
function Cell({
  label,
  value,
  unit,
  max = 730,
  disabled,
  onSave,
}: {
  /** The column's heading, said over the box on a phone, where there is none. */
  label: string;
  value: number;
  unit: string;
  max?: number;
  disabled?: boolean;
  onSave: (value: number) => void;
}) {
  return (
    <td className="px-2 py-1.5 text-right max-md:min-w-0 max-md:p-0 max-md:text-left">
      <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-faint md:hidden">
        {label}
      </span>
      <span className="inline-flex items-center justify-end gap-1.5 max-md:flex max-md:justify-start">
        <input
          type="number"
          min={1}
          max={max}
          aria-label={`${label}, ${unit}`}
          key={String(value)}
          defaultValue={value}
          disabled={disabled}
          onBlur={(event) => {
            const raw = event.target.value.trim();
            if (raw === "") return;
            const next = Number(raw);
            /* Silently ignored rather than corrected. Rejecting the write and
               leaving the box as typed lets somebody see what they entered;
               snapping it to a bound would look like the setting saved. */
            if (!Number.isFinite(next) || next < 1 || next > max) return;
            if (next !== value) onSave(next);
          }}
          className={cn(inputClass, "w-16 text-right max-md:w-full max-md:min-w-0")}
        />
        <span className="w-7 shrink-0 text-left text-[11px] text-faint">{unit}</span>
      </span>
    </td>
  );
}

/** "internal-sales" -> "Internal sales". The slug is the key, not the label. */
function teamName(slug: string): string {
  const words = slug.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
